import {ImportingDataTypes} from "../../types";
import {
    createInvalidPartSection,
    PreventionInvalidityCode,
    PreventativeAutocompleteType,
} from "./shared";
import {CODE_GROUP_FIELD_PREFIX} from "../shared/product-code-import-helpers";

const partNumberField = {
    key: 'partNumber',
    label: 'Part Number',
    type: PreventativeAutocompleteType.PART,
    modifyRowUsesPatchValue: true,
    extractValue: (o) => (typeof o === 'string' ? o : o?.partNo ?? null),
    modifyRow: (row, val) => ({
        ...row,
        partNumber: typeof val === 'string' ? val : val?.partNo ?? val,
    }),
    autocompleteProps: {
        placeholder: 'Search for part...',
        getOptionLabel: (o) => o?.partNo ?? '',
        isOptionEqualToValue: (o, v) => o?.partNo === v?.partNo,
    },
};

/**
 * @param {string} fieldKey
 * @param {Array<{name: string, title: string}>} mappingFields
 */
const createCodeGroupField = (fieldKey, mappingFields) => {
    const mapped = mappingFields?.find((f) => f.name === fieldKey);
    return {
        key: fieldKey,
        label: mapped?.title ?? fieldKey,
        type: 'input',
        modifyRow: (row, val) => ({...row, [fieldKey]: val}),
        extractValue: (o) => (typeof o === 'string' ? o : o ?? null),
    };
};

/**
 * @param {Array<Object>} issues
 * @param {Array<{name: string, title: string}>} mappingFields
 */
export const buildMissingProductCodeFields = (issues, mappingFields) => {
    const mappedGroupKeys = (mappingFields ?? [])
        .map((field) => field.name)
        .filter((name) => typeof name === 'string' && name.startsWith(CODE_GROUP_FIELD_PREFIX));

    return [
        partNumberField,
        ...mappedGroupKeys.map((key) => createCodeGroupField(key, mappingFields)),
    ];
};

/**
 * @param {Array<Object>} issues
 * @param {Array<{name: string, title: string}>} mappingFields
 */
export const buildInvalidProductCodeFields = (issues, mappingFields) => {
    const fieldKeys = [
        ...new Set(
            issues.flatMap((issue) => Object.keys(issue?.meta?.invalidCodesByGroup ?? {})),
        ),
    ].sort();

    return [
        {
            key: 'partNumber',
            label: 'Part Number',
            type: 'text',
            extractValue: (o) => (typeof o === 'string' ? o : o?.partNumber ?? null),
        },
        ...fieldKeys.map((key) => createCodeGroupField(key, mappingFields)),
    ];
};

export const preventativeInteraction = {
    datasourceId: ImportingDataTypes.productCodeAssignment,
    sections: [
        createInvalidPartSection('partNumber'),
        {
            sectionKey: 'missingProductCodes',
            sectionTitle: 'Missing Product Codes',
            invalidityCode: PreventionInvalidityCode.MISSING_REQUIRED_FIELDS,
            buildFields: buildMissingProductCodeFields,
        },
        {
            sectionKey: 'invalidProductCodes',
            sectionTitle: 'Invalid Product Codes',
            invalidityCode: PreventionInvalidityCode.INVALID_CODE,
            buildFields: buildInvalidProductCodeFields,
        },
    ],
};
